import {
  fetchCurrentUser,
  postLogout,
} from "@/services/apiClient";
import { clearTokens, getAccessToken, setTokens } from "@/services/authTokens";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

const AuthContext = createContext(null);

function decodeJwtPayload(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function buildFallbackUserFromAccessToken() {
  const token = getAccessToken();
  if (!token) return null;
  const p = decodeJwtPayload(token);
  if (!p || typeof p !== "object") return null;
  if (!p.sub || !p.email) return null;
  return {
    user_id: String(p.sub),
    email: String(p.email),
    email_verified: true,
    phone: null,
    phone_verified: false,
    profile: { first_name: "", last_name: "" },
    status: "active",
    roles: Array.isArray(p.roles) ? p.roles : [],
  };
}

export function AuthProvider({ children }) {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const raw = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : "";
    if (!raw) return;
    const params = new URLSearchParams(raw);
    const access = params.get("access_token");
    const refresh = params.get("refresh_token");
    if (!access || !refresh) return;

    setTokens(access, refresh);
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}`,
    );
  }, [location.pathname]);

  const loadSession = useCallback(async () => {
    setReady(false);
    if (!getAccessToken()) {
      setUser(null);
      setReady(true);
      return;
    }
    try {
      const me = await fetchCurrentUser();
      setUser(me);
    } catch (err) {
      if (err?.status === 401) {
        setUser(null);
      } else {
        // Keep authenticated UX when backend /me is temporarily unavailable.
        setUser(buildFallbackUserFromAccessToken());
      }
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    if (location.pathname === "/auth/callback") {
      return;
    }
    let cancelled = false;
    (async () => {
      setReady(false);
      if (!getAccessToken()) {
        if (!cancelled) {
          setUser(null);
          setReady(true);
        }
        return;
      }
      try {
        const me = await fetchCurrentUser();
        if (!cancelled) setUser(me);
      } catch (err) {
        if (cancelled) return;
        if (err?.status === 401) {
          setUser(null);
        } else {
          setUser(buildFallbackUserFromAccessToken());
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const logout = useCallback(async () => {
    await postLogout();
    clearTokens();
    setUser(null);
    window.location.href = "/login";
  }, []);

  const value = useMemo(
    () => ({
      user,
      ready,
      isAuthenticated: Boolean(user),
      logout,
      reloadUser: loadSession,
    }),
    [user, ready, logout, loadSession],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
